import {extractClinicLocationToDisplay, Offer, OfferContent, OfferStaffingPlan} from 'offer';
import React, {useEffect, useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {
    isClinicRequired,
    isLanguageRequired,
    isTreatmentOfferedCareMedicated,
    isTreatmentOfferedPractitionerMedicated,
    Referral
} from 'referral';
import {Observable, Subject} from 'rxjs';
import {useFormSubmissionSignal$, useFormValidationSignal$} from 'rxsupport';
import {ClinicSection} from './ClinicSection';
import {CommentInput} from './CommentInput';
import {FirstServiceDateInput} from './FirstServiceDateInput';
import {ServiceInRequiredLanguageInput} from './ServiceInRequiredLanguageInput';
import {SiblingReminder} from './siblings/SiblingReminder';
import {presetTreatmentOfferedBasedOnServiceTypes, TreatmentHourSection} from './TreatmentHourSection';
import {PsychotherapySection} from "./PsychotherapyOfferSection";
import {ServiceLocationTypeInput} from "./ServiceLocationTypeInput";
import _ from "lodash";
import {OfferStaffingPlanInput} from "./OfferStaffingPlanInput";
import {
    caregiverLocationDisplay,
    offerHoursDisplayCondition,
    referralCaregiverLocations,
    shouldRequireClinicInputBySelectedLocation
} from "../domain/offer";


export type OfferFormInput = Record<keyof Omit<OfferContent, 'clinic'>, string | null> & {
    clinicLocationGuid: string | null,
    providerGuid?: string | null // internal value use only in frontend side
    draftStaffingPlan: OfferStaffingPlan | null;
};

const cleanDraftStaffingPlan = (draftStaffingPlan: OfferStaffingPlan | null): OfferStaffingPlan | null => {
    if (draftStaffingPlan === null) {
        return null;
    }
    return _.pick(draftStaffingPlan,
        'comments',
        'offeredParaprofessionalHours',
        'offeredMidLevelHours',
        'offeredHighLevelHours',
        'offeredGroupCareHours',
        'fullOfferStartDate',
        'reviewedAndApprovedByClientFamilyDate'
    );
}

export function sanitizeOfferInput(input: OfferFormInput): OfferFormInput {
    input.directCareHours = input.directCareHours === "" ? null : input.directCareHours;
    input.midLevelSupervisionHours = input.midLevelSupervisionHours === "" ? null : input.midLevelSupervisionHours;
    input.highLevelSupervisionHours = input.highLevelSupervisionHours === "" ? null : input.highLevelSupervisionHours;
    input.groupCareHours = input.groupCareHours === "" ? null : input.groupCareHours;
    input.draftStaffingPlan = cleanDraftStaffingPlan(input.draftStaffingPlan ?? null);

    // remove invalid input before submit clinic setting
    if (input.clinicLocationGuid !== '__OTHER__') {
        input.otherClinicLocation = null; // should be null if clinicLocationGuid is not null
    }
    if (input.provideServicesInClinic === 'false') {
        input.clinicLocationGuid = null;
        input.otherClinicLocation = null;
    }


    return _.omit(input, ['providerGuid']);
}

export type OfferFormControlSignal = SubmissionSignal & ValidationSignal;
type SubmissionSignal = {
    submitPlease$: Observable<boolean>;
    isSubmitting$: Observable<boolean>;
    submitOperation: (validatedInput: OfferFormInput) => void | Promise<void>;
};
type ValidationSignal = {
    validatePlease$: Observable<boolean>;
    validationResult$: Subject<boolean>;
};

export type OfferFormProps = {
    referral: Referral;
    existingOffer?: Partial<Offer>;
    className?: string;
    providerInput?: React.ReactElement | null;
} & OfferFormControlSignal;
export const OfferForm = ({
                              referral,
                              existingOffer,
                              validatePlease$,
                              validationResult$,
                              isSubmitting$,
                              submitPlease$,
                              submitOperation,
                              className,
                              providerInput = null,
                          }: OfferFormProps
) => {
    const methods = useForm<OfferFormInput, object>({
        defaultValues: {
            provideServicesInClinic: existingOffer?.provideServicesInClinic?.toString() ?? null,
            clinicLocationGuid: extractClinicLocationToDisplay(existingOffer ?? null).id,
            otherClinicLocation: existingOffer?.otherClinicLocation ?? null,
            provideServiceInRequiredLanguage: existingOffer?.provideServiceInRequiredLanguage?.toString() ?? null,
            provideAllEstimatedHours: existingOffer?.provideAllEstimatedHours?.toString() ?? null,
            firstServiceDate: existingOffer?.firstServiceDate ?? null,
            directCareHours: existingOffer?.directCareHours?.toString() ?? null,
            midLevelSupervisionHours: existingOffer?.midLevelSupervisionHours?.toString() ?? null,
            highLevelSupervisionHours: existingOffer?.highLevelSupervisionHours?.toString() ?? null,
            groupCareHours: existingOffer?.groupCareHours?.toString() ?? null,
            lastServiceDate: existingOffer?.lastServiceDate?.toString() ?? null,
            comments: existingOffer?.comments ?? null,
            caregiverMediatedMidLevelHours: existingOffer?.caregiverMediatedMidLevelHours?.toString() ?? null,
            caregiverMediatedHighLevelHours: existingOffer?.caregiverMediatedHighLevelHours?.toString() ?? null,
            treatmentOffered: existingOffer?.treatmentOffered ?? null,
            startDateCaregiverMediatedTreatment: existingOffer?.startDateCaregiverMediatedTreatment ?? null,
            startDatePractMediatedTreatment: existingOffer?.startDatePractMediatedTreatment ?? null,
            psychotherapyOffer: existingOffer?.psychotherapyOffer?.toString() ?? null,
            serviceLocationType: existingOffer?.serviceLocationType,
            serviceLocationParaprofessionalType: existingOffer?.serviceLocationParaprofessionalType,
            serviceLocationMidLevelType: existingOffer?.serviceLocationMidLevelType,
            serviceLocationHighLevelType: existingOffer?.serviceLocationHighLevelType,
            serviceLocationGroupCareType: existingOffer?.serviceLocationGroupCareType,
            draftStaffingPlan: existingOffer?.draftStaffingPlan ?? null,
        }
    });
    useFormValidationSignal$({
        doItPlease$: validatePlease$,
        putResultHere$: validationResult$,
        useFormResult: methods,
    });
    useFormSubmissionSignal$({
        doItPlease$: submitPlease$,
        submit: submitOperation,
        useFormResult: methods,
    });

    const {watch, setValue} = methods;
    const presetTreatmentOffered = useMemo(() => presetTreatmentOfferedBasedOnServiceTypes(referral), [referral]);
    const treatmentOfferedInput = watch('treatmentOffered');
    // The form value is null on the first render, so fall back to the referral's preset.
    const treatmentOffered = treatmentOfferedInput || presetTreatmentOffered;
    const locationCond = caregiverLocationDisplay(
        referralCaregiverLocations(referral), treatmentOffered);
    const isClinicRequiredByReferral = isClinicRequired(referral);

    const requireClinicLocationInput = shouldRequireClinicInputBySelectedLocation(isClinicRequiredByReferral, [
        methods.watch('serviceLocationType'),
        methods.watch('serviceLocationParaprofessionalType'),
        methods.watch('serviceLocationMidLevelType'),
        methods.watch('serviceLocationHighLevelType'),
        methods.watch('serviceLocationGroupCareType')
    ]);

    useEffect(() => {
        if (presetTreatmentOffered != null && treatmentOfferedInput === null) {
            setValue('treatmentOffered', presetTreatmentOffered);
        }

        if (isTreatmentOfferedPractitionerMedicated(treatmentOfferedInput)) {
            setValue("caregiverMediatedMidLevelHours", null);
            setValue("caregiverMediatedHighLevelHours", null);
            setValue("startDateCaregiverMediatedTreatment", null);
        }

        if (isTreatmentOfferedCareMedicated(treatmentOfferedInput)) {
            setValue("startDatePractMediatedTreatment", null);
            setValue("directCareHours", null);
            setValue("midLevelSupervisionHours", null);
            setValue("highLevelSupervisionHours", null);
            setValue("groupCareHours", null);
        }

        // An older offer still holds an assessment location whose dropdown a per-level one has
        // taken over, and react-hook-form would save that value again. Drop it.
        if (locationCond.assessmentReplaced) {
            setValue('serviceLocationType', null);
        }

    }, [setValue, treatmentOfferedInput, presetTreatmentOffered, locationCond.assessmentReplaced])

    useEffect(() => {
        if (requireClinicLocationInput) {
            setValue('provideServicesInClinic', 'true');
        } else if (!isClinicRequiredByReferral) {
            setValue('provideServicesInClinic', 'false');
        }

    }, [requireClinicLocationInput, setValue, isClinicRequiredByReferral]);

    const offerHoursDisplayCond = offerHoursDisplayCondition(treatmentOffered, presetTreatmentOffered == null, referral);
    return (
        <FormProvider {...methods}>
            <form className={`w-full py-2 ${className ?? ''}`}>
                {providerInput !== null && providerInput}
                <ClinicSection referral={referral}
                               isSubmitting$={isSubmitting$}
                               requireClinicLocationInput={requireClinicLocationInput}/>

                {isLanguageRequired(referral) &&
                    <ServiceInRequiredLanguageInput referral={referral} isSubmitting$={isSubmitting$}/>
                }

                <FirstServiceDateInput referral={referral} isSubmitting$={isSubmitting$}/>

                <PsychotherapySection referral={referral} isSubmitting$={isSubmitting$}/>
                {locationCond.showAssessment &&
                    <div className={'mb-2'}>
                        <ServiceLocationTypeInput fieldName={'serviceLocationType'} referral={referral}
                                                  isSubmitting$={isSubmitting$}
                                                  permittedLocations={referral.serviceLocations}/>
                    </div>
                }
                <TreatmentHourSection referral={referral}
                                      hoursCond={offerHoursDisplayCond}
                                      locationCond={locationCond}
                                      isSubmitting$={isSubmitting$}
                />
                <OfferStaffingPlanInput referral={referral}
                                        hoursCond={offerHoursDisplayCond}
                                        isSubmitting$={isSubmitting$}/>

                <CommentInput isSubmitting$={isSubmitting$}/>

                <SiblingReminder referral={referral}/>
            </form>
        </FormProvider>
    );
};
